import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import { cinemaTone } from "@/lib/cinemaTone";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";

import { useCity } from "@/contexts/CityContext";
import { currentIntlLocale } from "@/i18n/config";
import { madridMinutesNow, madridToday } from "@/seo/shared";

import {
  coverImageUrl,
  EDITORIAL_RAIL_KEYS,
  fetchProgramming,
  formatTime,
  focalPosition,
  imageSrcSet,
  movieHref,
  sizedImageUrl,
  type EditorialRail,
  type RailMovie,
  type ScreeningCard,
} from "@/lib/queries";
import { RailArrow, useRailScroller } from "@/components/RailArrows";

/**
 * Landing rails: a flat list of editorial-tag carousels (no subcategories).
 * Each rail is a horizontal carousel of poster cards with left/right arrows
 * (desktop) and native touch scroll (mobile).
 */
export function EditorialRails({
  rails,
  loading,
}: {
  rails: EditorialRail[];
  loading: boolean;
}) {
  const { t } = useTranslation();
  const nextByMovie = useNextScreenings();
  if (!loading && rails.length === 0) return null;

  return (
    <section id="explora" className="py-16 md:py-24 bg-white border-t-2 border-rc-navy">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="mb-10">
          <h2 className="font-display text-4xl md:text-6xl uppercase leading-[0.9] tracking-[-0.03em]">
            {t("rails.heading")}
          </h2>
        </div>

        {loading ? (
          <RailsSkeleton />
        ) : (
          <div className="space-y-14">
            {rails.map((rail) => (
              <Rail key={rail.label} rail={rail} nextByMovie={nextByMovie} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function Rail({ rail, nextByMovie }: { rail: EditorialRail; nextByMovie: Map<string, ScreeningCard> }) {
  const { t } = useTranslation();
  const tagKey = EDITORIAL_RAIL_KEYS[rail.label as keyof typeof EDITORIAL_RAIL_KEYS];
  const { scrollerRef, canLeft, canRight, nudge } = useRailScroller(rail.movies.length);

  return (
    <div>
      <h3 className="data-voice mb-4 inline-block rounded-full bg-rc-lavender px-4 py-2 text-sm font-bold md:px-5 md:text-base">
        {tagKey ? t(`rails.tags.${tagKey}`, { defaultValue: rail.label }) : rail.label}
      </h3>

      <div className="relative">
        <RailArrow dir="left" show={canLeft} onClick={() => nudge(-1)} />
        <RailArrow dir="right" show={canRight} onClick={() => nudge(1)} />

        <div
          ref={scrollerRef}
          className="flex gap-5 overflow-x-auto pb-2 snap-x snap-proximity overscroll-x-contain [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
        >
          {rail.movies.map((movie) => (
            <RailCard key={movie.id} movie={movie} next={nextByMovie.get(movie.id)} />
          ))}
        </div>
      </div>
    </div>
  );
}

/** El próximo pase de cada película en la ciudad elegida. Sale de la misma
 *  consulta de cartelera que «Hoy en…» (misma caché): no añade peticiones. */
function useNextScreenings(): Map<string, ScreeningCard> {
  const { city } = useCity();
  const { data } = useQuery<ScreeningCard[]>({
    queryKey: ["screenings", "programming"],
    queryFn: fetchProgramming,
    staleTime: 60_000,
  });
  return useMemo(() => {
    const next = new Map<string, ScreeningCard>();
    if (!data) return next;
    const today = madridToday();
    const now = madridMinutesNow();
    for (const s of data) {
      if (city && s.cinemas?.city !== city) continue;
      if (s.screening_date < today) continue;
      if (s.screening_date === today) {
        const [h, m] = s.screening_time.split(":").map(Number);
        if (h * 60 + m < now) continue;
      }
      const cur = next.get(s.movie_id);
      if (!cur || `${s.screening_date}T${s.screening_time}` < `${cur.screening_date}T${cur.screening_time}`) next.set(s.movie_id, s);
    }
    return next;
  }, [data, city]);
}

/** «Hoy 19:00», «Mañana 17:30», «Sáb 1 · 18:00». */
function nextLabel(s: ScreeningCard, t: TFunction): string {
  const today = madridToday();
  const tomorrow = new Date(`${today}T12:00:00Z`);
  tomorrow.setUTCDate(tomorrow.getUTCDate() + 1);
  const time = formatTime(s.screening_time);
  if (s.screening_date === today) return `${t("movies.dates.today")} ${time}`;
  if (s.screening_date === tomorrow.toISOString().slice(0, 10)) return `${t("movies.dates.tomorrow")} ${time}`;
  const d = new Date(`${s.screening_date}T12:00:00Z`);
  const wd = d.toLocaleDateString(currentIntlLocale(), { weekday: "short", timeZone: "UTC" }).replace(".", "");
  return `${wd.charAt(0).toUpperCase()}${wd.slice(1)} ${d.getUTCDate()} · ${time}`;
}

function RailCard({ movie, next }: { movie: RailMovie; next?: ScreeningCard }) {
  const { t } = useTranslation();
  const image = coverImageUrl({ movie_images: movie.movie_images });

  return (
    <Link
      to={movieHref(movie, movie.city)}
      className="group block shrink-0 snap-start w-[216px] sm:w-[240px] md:w-[264px] lg:w-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rc-violet focus-visible:ring-offset-2"
    >
      {/* Exploración: a dos tonos sobre un color de la paleta; en color al pasar el ratón. */}
      <div className={`relative isolate aspect-[2/3] overflow-hidden ${cinemaTone(movie.id).duo}`}>
        {/* Redimensionada (los pósters originales pesan hasta varios MB) y con el punto focal. */}
        <img
          src={sizedImageUrl(image, { width: 528, height: 792, focal: movie })}
          srcSet={imageSrcSet(image, [264, 528, 792], 1.5, movie)}
          sizes="(max-width: 640px) 216px, (max-width: 768px) 240px, (max-width: 1024px) 264px, 299px"
          alt=""
          loading="lazy"
          style={{ objectPosition: focalPosition(movie) }}
          className="duotone absolute inset-0 w-full h-full object-cover transition-[filter,transform] duration-300 ease-spring group-hover:scale-[1.04] group-hover:mix-blend-normal group-hover:grayscale-0 group-hover:contrast-100"
        />
      </div>
      <div className="mt-2.5 font-display text-xl uppercase leading-[0.95] tracking-[-0.01em] text-rc-navy line-clamp-2">
        {movie.title}
      </div>
      {/* El próximo pase, en una línea pequeña como en «Hoy en…». */}
      {next && (
        <div className="data-voice mt-1.5 text-[10px] font-bold text-rc-navy/70 line-clamp-1">
          {nextLabel(next, t)}
          {next.cinemas?.name ? ` · ${next.cinemas.name}` : ""}
        </div>
      )}
    </Link>
  );
}

function RailsSkeleton() {
  return (
    <div className="space-y-14">
      {Array.from({ length: 3 }).map((_, r) => (
        <div key={r}>
          <div className="h-9 md:h-10 w-48 md:w-64 rounded-full bg-rc-navy/10 animate-pulse mb-4" />
          <div className="flex gap-5 overflow-hidden">
            {Array.from({ length: 5 }).map((_, i) => (
              <div
                key={i}
                className="shrink-0 w-[216px] sm:w-[240px] md:w-[264px] lg:w-card aspect-[2/3] bg-rc-navy/5 animate-pulse"
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
